import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { loadGoogleMaps } from '../../../shared/helpers/google-maps-loader';
import { Icons } from '../../../shared/helpers/icons';
import {
  ActionUser,
  Geofence,
  LayerIcon,
  Style,
  StyleAction,
  StyleActionCreatePayload,
  StyleEditFormState,
  StyleRule,
  StyleRuleCreatePayload,
  StyleUpdatePayload
} from '../../../shared/models/agent';

export type StyleModalMode = 'add' | 'edit';

/** Parses a WKT POLYGON into Google Maps rings. v2 stores coordinates as
 *  "lng lat" pairs (Agents.vb reads arr(0)=lng, arr(1)=lat). Returns [] for
 *  anything unparseable so callers can fall back gracefully. */
function parseWktPolygon(wkt: string): google.maps.LatLngLiteral[][] {
  const m = /^\s*POLYGON\s*\((.*)\)\s*$/is.exec(wkt);
  if (!m) return [];
  return m[1]
    .split(/\)\s*,\s*\(/)
    .map(ring =>
      ring
        .replace(/[()]/g, '')
        .split(',')
        .map(pair => {
          const [x, y] = pair.trim().split(/\s+/).map(Number);
          return { lat: y, lng: x };
        })
        .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    )
    .filter(ring => ring.length >= 3);
}

// v2's AddStyle UI defaults (Edit-Style.aspx.vb AddStyle()): differ slightly
// from the INSERT defaults v2 used (those drove the seeded rows). Match the
// UI ones so the user sees a sensible starter form on open in add mode.
function blankForm(): StyleEditFormState {
  return {
    styleId: 0,
    name: '',
    fillColor: '#000000',
    fillOpacity: '1',
    strokeColor: '#000000',
    strokeWeight: 1,
    strokeOpacity: '1',
    zIndex: 50,
    minZoom: 1,
    maxZoom: 30,
    labelFontSize: 12,
    labelMinZoom: 20,
    labelMaxZoom: 30,
    labelAlign: 'C',
    iconAlign: 'C',
    geoPoint: false,
    geoLine: false,
    geoPolygon: false,
    icon: '',
    iconWidth: null,
    iconHeight: null
  };
}

@Component({
  selector: 'style-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './style-modal.component.html'
})
export class StyleModalComponent implements OnChanges {
  icons = Icons;

  // v2 also offers 'route' but it's intentionally dropped here.
  readonly ruleFieldNames: readonly string[] = ['code', 'speed', 'geofence'];
  readonly ruleConditions: readonly string[] = ['=', '!=', '<', '>', '<=', '>='];
  // v2 (Edit-Style.aspx.vb): a geofence rule's only operator is "Within",
  // and its value is a geofence polygon picked from the building's list.
  readonly geofenceCondition = 'Within';
  readonly actionNames: readonly { value: string; label: string }[] = [
    { value: 'SMS',   label: 'Send SMS' },
    { value: 'EMAIL', label: 'Send Email' }
  ];

  @Input() mode: StyleModalMode = 'add';
  @Input() open = false;
  @Input() saving = false;
  /** Only consulted in edit mode. */
  @Input() initialStyle: Style | null = null;
  @Input() icons$list: LayerIcon[] = [];
  @Input() iconsLoading = false;
  @Input() iconUploading = false;
  /** Only meaningful in edit mode. */
  @Input() rules: StyleRule[] = [];
  @Input() actions: StyleAction[] = [];
  @Input() ruleSaving = false;
  @Input() actionSaving = false;
  /** Building-scoped geofence polygons for the "geofence" rule value picker. */
  @Input() geofences: Geofence[] = [];
  @Input() geofencesLoading = false;
  /** Building-scoped users pickable as an action's alert recipient. */
  @Input() actionUsers: ActionUser[] = [];
  @Input() actionUsersLoading = false;

  @Output() save = new EventEmitter<StyleUpdatePayload>();
  @Output() closeModal = new EventEmitter<void>();
  @Output() iconFileChosen = new EventEmitter<File>();
  @Output() addRule = new EventEmitter<StyleRuleCreatePayload>();
  @Output() deleteRule = new EventEmitter<StyleRule>();
  @Output() addAction = new EventEmitter<StyleActionCreatePayload>();
  @Output() deleteAction = new EventEmitter<StyleAction>();

  form = signal<StyleEditFormState | null>(null);
  newRuleDraft = signal<StyleRuleCreatePayload | null>(null);
  newActionDraft = signal<StyleActionCreatePayload | null>(null);

  // ---- Geofence map preview (mirrors v2's polygon thumbnails, but as a
  // live Google Map). The div only exists while a geofence is selected in
  // the draft row, so the ViewChild setter is our create/destroy hook.
  previewUnavailable = signal(false);
  private previewEl: HTMLDivElement | null = null;
  private previewMap: google.maps.Map | null = null;
  private previewPolygon: google.maps.Polygon | null = null;

  @ViewChild('geofencePreview')
  set geofencePreviewRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.previewEl = ref?.nativeElement ?? null;
    if (!this.previewEl) {
      // *ngIf tore the div down — drop the map bound to it.
      this.previewMap = null;
      this.previewPolygon = null;
      return;
    }
    void this.renderGeofencePreview();
  }
  /** Fields the user has interacted with (blurred at least once). We only
   *  show validation errors for touched fields so they don't flash on open. */
  touched = signal<Set<string>>(new Set<string>());

  // Rules + Actions are only meaningful in edit mode (need a styleId).
  // Also hidden in edit mode for the catch-all "default" style — matches
  // v2's Manage-Live-Details editStyle() behaviour.
  readonly showRulesAndActions = computed<boolean>(() => {
    if (this.mode === 'add') return false;
    const name = this.form()?.name?.trim().toLowerCase();
    return name !== 'default';
  });

  /** False when a required field is empty — drives the submit-button
   *  disabled state so the user can see they can't save yet. */
  readonly canSubmit = computed<boolean>(() => !!this.form()?.name?.trim());

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] || changes['initialStyle'] || changes['mode']) {
      if (!this.open) {
        this.form.set(null);
        this.newRuleDraft.set(null);
        this.newActionDraft.set(null);
        this.touched.set(new Set<string>());
        return;
      }
      if (this.mode === 'edit' && this.initialStyle) {
        this.form.set(this.toFormState(this.initialStyle));
      } else if (this.mode === 'add') {
        this.form.set(blankForm());
      }
      this.newRuleDraft.set(null);
      this.newActionDraft.set(null);
      // Fresh open = clean slate; no fields touched yet.
      this.touched.set(new Set<string>());
    }
  }

  markTouched(field: string): void {
    this.touched.update(s => {
      if (s.has(field)) return s;
      const next = new Set(s);
      next.add(field);
      return next;
    });
  }

  showError(field: string, invalid: boolean): boolean {
    return invalid && this.touched().has(field);
  }

  private toFormState(s: Style): StyleEditFormState {
    return {
      styleId: s.id,
      name: s.name ?? '',
      fillColor: s.fillColor ?? '#000000',
      fillOpacity: s.fillOpacity ?? '1',
      strokeColor: s.strokeColor ?? '#000000',
      strokeWeight: s.strokeWeight ?? 1,
      strokeOpacity: s.strokeOpacity ?? '1',
      zIndex: s.zIndex ?? 50,
      minZoom: s.minZoom ?? 1,
      maxZoom: s.maxZoom ?? 30,
      labelFontSize: s.labelFontSize ?? 12,
      labelMinZoom: s.labelMinZoom ?? 20,
      labelMaxZoom: s.labelMaxZoom ?? 30,
      labelAlign: s.labelAlign ?? 'C',
      iconAlign: s.iconAlign ?? 'C',
      geoPoint: !!s.geoTypes && s.geoTypes.includes('01'),
      geoLine: !!s.geoTypes && s.geoTypes.includes('23'),
      geoPolygon: !!s.geoTypes && s.geoTypes.includes('45'),
      icon: s.icon ?? '',
      iconWidth: s.width,
      iconHeight: s.height
    };
  }

  updateField<K extends keyof StyleEditFormState>(
    key: K,
    value: StyleEditFormState[K]
  ): void {
    this.form.update(f => (f ? { ...f, [key]: value } : f));
  }

  onIconPicked(url: string): void {
    if (!url) {
      this.updateField('icon', '');
      this.updateField('iconWidth', null);
      this.updateField('iconHeight', null);
      return;
    }
    const picked = this.icons$list.find(i => i.url === url);
    this.updateField('icon', url);
    this.updateField('iconWidth', picked?.width ?? null);
    this.updateField('iconHeight', picked?.height ?? null);
  }

  onIconFileChange(ev: Event): void {
    const file = (ev.target as HTMLInputElement).files?.[0] ?? null;
    if (!file) return;
    this.iconFileChosen.emit(file);
    (ev.target as HTMLInputElement).value = '';
  }

  /** Parent calls this after a successful icon upload so the new file
   *  becomes the currently-selected icon on the form. */
  selectUploadedIcon(icon: LayerIcon): void {
    this.updateField('icon', icon.url);
    this.updateField('iconWidth', icon.width);
    this.updateField('iconHeight', icon.height);
  }

  private encodeGeoTypes(p: boolean, l: boolean, poly: boolean): string {
    return (p ? '01' : '') + (l ? '23' : '') + (poly ? '45' : '');
  }

  onSubmit(): void {
    const f = this.form();
    if (!f) return;
    if (!f.name.trim()) return;
    this.save.emit({
      name: f.name.trim(),
      fillColor: f.fillColor || null,
      fillOpacity: f.fillOpacity || null,
      strokeColor: f.strokeColor || null,
      strokeWeight: f.strokeWeight,
      strokeOpacity: f.strokeOpacity || null,
      zIndex: f.zIndex,
      minZoom: f.minZoom,
      maxZoom: f.maxZoom,
      labelFontSize: f.labelFontSize,
      labelMinZoom: f.labelMinZoom,
      labelMaxZoom: f.labelMaxZoom,
      labelAlign: f.labelAlign || null,
      iconAlign: f.iconAlign || null,
      geoTypes: this.encodeGeoTypes(f.geoPoint, f.geoLine, f.geoPolygon),
      icon: f.icon || null,
      width: f.iconWidth,
      height: f.iconHeight
    });
  }

  onClose(): void {
    if (this.saving) return;
    this.closeModal.emit();
  }

  // ---- Rules inline CRUD ------------------------------------------------

  startAddRule(): void {
    this.newRuleDraft.set({ fieldName: 'code', fieldValue: '', fieldCondition: '=' });
  }

  cancelAddRule(): void {
    if (this.ruleSaving) return;
    this.newRuleDraft.set(null);
  }

  updateRuleDraft<K extends keyof StyleRuleCreatePayload>(
    key: K,
    value: StyleRuleCreatePayload[K]
  ): void {
    this.newRuleDraft.update(d => (d ? { ...d, [key]: value } : d));
    if (key === 'fieldValue') {
      this.previewUnavailable.set(false);
      // If the preview div already exists (switching between geofences) the
      // ViewChild setter won't refire — re-render explicitly.
      void this.renderGeofencePreview();
    }
  }

  /** Geofence currently picked in the draft row — drives the map preview. */
  draftGeofence(): Geofence | null {
    const d = this.newRuleDraft();
    if (!d || d.fieldName !== 'geofence' || !d.fieldValue) return null;
    return this.geofences.find(g => String(g.id) === d.fieldValue) ?? null;
  }

  private async renderGeofencePreview(): Promise<void> {
    const gf = this.draftGeofence();
    const el = this.previewEl;
    if (!gf || !el) return;
    const paths = gf.geo ? parseWktPolygon(gf.geo) : [];
    if (!paths.length) {
      this.previewUnavailable.set(true);
      return;
    }
    try {
      const g = await loadGoogleMaps();
      // The div may have been torn down (or selection changed) while the
      // maps script loaded.
      if (this.previewEl !== el || !el.isConnected) return;
      if (!this.previewMap) {
        this.previewMap = new g.maps.Map(el, {
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });
      }
      this.previewPolygon?.setMap(null);
      this.previewPolygon = new g.maps.Polygon({
        paths,
        map: this.previewMap,
        strokeColor: '#46b8da',
        strokeWeight: 2,
        fillColor: '#46b8da',
        fillOpacity: 0.25,
        clickable: false
      });
      const bounds = new g.maps.LatLngBounds();
      for (const ring of paths) for (const p of ring) bounds.extend(p);
      this.previewMap.fitBounds(bounds, 16);
    } catch {
      this.previewUnavailable.set(true);
    }
  }

  /** Mirrors v2's ddlFieldName_SelectedIndexChanged: switching to "geofence"
   *  locks the operator to "Within" and swaps the value input for the
   *  geofence picker; switching away restores the plain operator/value. */
  onRuleFieldNameChange(fieldName: string): void {
    this.newRuleDraft.update(d => {
      if (!d) return d;
      const wasGeofence = d.fieldName === 'geofence';
      const isGeofence = fieldName === 'geofence';
      if (isGeofence === wasGeofence) return { ...d, fieldName };
      return {
        ...d,
        fieldName,
        fieldCondition: isGeofence ? this.geofenceCondition : '=',
        fieldValue: ''
      };
    });
  }

  /** Operators offered for a field — geofence rules only support "Within". */
  conditionsFor(fieldName: string | null | undefined): readonly string[] {
    return fieldName === 'geofence' ? [this.geofenceCondition] : this.ruleConditions;
  }

  /** A geofence rule stores the polygon's Features.id as fieldValue — show
   *  its name in the rules table when we have it in the picker list. */
  ruleValueLabel(rule: StyleRule): string {
    if (!rule.fieldValue) return '—';
    if (rule.fieldName !== 'geofence') return rule.fieldValue;
    const gf = this.geofences.find(g => String(g.id) === rule.fieldValue);
    return gf ? (gf.name || `Geofence #${gf.id}`) : `Geofence #${rule.fieldValue}`;
  }

  onSaveRule(): void {
    const d = this.newRuleDraft();
    if (!d) return;
    if (!d.fieldName || !d.fieldValue.trim()) return;
    const isGeofence = d.fieldName === 'geofence';
    this.addRule.emit({
      fieldName: d.fieldName,
      fieldValue: d.fieldValue.trim(),
      fieldCondition: isGeofence ? this.geofenceCondition : d.fieldCondition || '='
    });
  }

  /** Parent calls this after a successful rule POST. */
  clearRuleDraft(): void {
    this.newRuleDraft.set(null);
  }

  // ---- Actions inline CRUD ----------------------------------------------

  startAddAction(): void {
    this.newActionDraft.set({ actionName: 'SMS', actionField: null, userId: null });
  }

  cancelAddAction(): void {
    if (this.actionSaving) return;
    this.newActionDraft.set(null);
  }

  updateActionDraft<K extends keyof StyleActionCreatePayload>(
    key: K,
    value: StyleActionCreatePayload[K]
  ): void {
    this.newActionDraft.update(d => (d ? { ...d, [key]: value } : d));
  }

  onSaveAction(): void {
    const d = this.newActionDraft();
    if (!d) return;
    // The alert sender resolves the recipient from userId alone, so a user
    // pick is required; actionField is legacy and no longer written.
    if (!d.actionName || d.userId == null) return;
    this.addAction.emit({
      actionName: d.actionName,
      actionField: null,
      userId: d.userId
    });
  }

  /** Dropdown label for a pickable user — appends the phone/email the
   *  alert would actually go to, so dead-end picks are visible upfront. */
  actionUserLabel(u: ActionUser, actionName: string | null | undefined): string {
    const name = u.name || `User #${u.id}`;
    if (actionName === 'SMS') return `${name}${u.phone ? ' — ' + u.phone : ' — no phone!'}`;
    if (actionName === 'EMAIL') return `${name}${u.email ? ' — ' + u.email : ' — no email!'}`;
    return name;
  }

  /** Recipient shown in the actions table: the user's name when we know it,
   *  falling back to the legacy free-text target or raw user id. */
  actionTargetLabel(a: StyleAction): string {
    if (a.userId != null) {
      const u = this.actionUsers.find(x => x.id === a.userId);
      if (u) return u.name || `User #${u.id}`;
    }
    return a.actionField || (a.userId != null ? `User #${a.userId}` : '—');
  }

  /** Parent calls this after a successful action POST. */
  clearActionDraft(): void {
    this.newActionDraft.set(null);
  }

  actionLabelFor(value: string | null | undefined): string {
    if (!value) return '—';
    return this.actionNames.find(a => a.value === value)?.label ?? value;
  }
}
